import React from 'react';
import { Routes, Route, NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { usePermissions } from './hooks/usePermissions';
import LoginPage from './pages/LoginPage';
import Snowfall from 'react-snowfall';
import './App.css';

// Импортируем компоненты панелей
import DashboardPanel from './components/DashboardPanel';
import ServersPanel from './components/ServersPanel';
import PagesPanel from './components/PagesPanel';
import GeneratorPanel from './components/GeneratorPanel';
import NotificationsPanel from './components/NotificationsPanel';
import DomainsPanel from './components/DomainsPanel';
import SettingsPanel from './components/SettingsPanel';
import ProfilePanel from './components/ProfilePanel';
import UsersPanel from './components/UsersPanel';
import InfoPanel from './components/InfoPanel';

// Импортируем иконки
import {
  BarChart3,
  Server,
  Layers,
  Bell,
  Globe,
  Settings,
  LogOut,
  Loader2,
  User,
  Users,
  Sparkles,
  Lock,
  Crown,
  ArrowUpRight,
  Activity
} from 'lucide-react';

// Spinner Cube Component
const SpinnerCube: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div className="spinner" onClick={onClick} title="Click to change theme">
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
  </div>
);

const AuthenticatedLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { canAccess, requiredPlan } = usePermissions();
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [upgradeModal, setUpgradeModal] = React.useState<{ show: boolean; feature: string; plan: string }>({
    show: false,
    feature: '',
    plan: '',
  });
  const [snowEnabled, setSnowEnabled] = React.useState(() => {
    const saved = localStorage.getItem('snowEnabled');
    return saved === 'true';
  });

  // Listen for snow state changes from Settings
  React.useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('snowEnabled');
      setSnowEnabled(saved === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check periodically for same-tab changes
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const userRole = (user?.is_expired && user?.role !== 'admin') ? 'starter' : (user?.role || 'user');
  const isPremium = ['pro', 'enterprise', 'admin'].includes(userRole);

  // Theme class for body
  React.useEffect(() => {
    document.body.className = `theme-${theme}`;
  }, [theme]);

  // Load unread notifications count
  React.useEffect(() => {
    if (!user) return;

    const loadUnreadCount = async () => {
      try {
        const response = await fetch('/api/notifications/unread-count', {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setUnreadCount(data.count);
        }
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);

    window.addEventListener('notificationsCleared', loadUnreadCount);
    window.addEventListener('notificationRead', loadUnreadCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsCleared', loadUnreadCount);
      window.removeEventListener('notificationRead', loadUnreadCount);
    };
  }, [user]);

  // Early return AFTER hooks
  if (!user) return <Navigate to="/login" replace />;

  const navItems = [
    { to: '/', icon: BarChart3, label: 'Dashboard' },
    { to: '/generator', icon: Sparkles, label: 'Generator' },
    { to: '/pages', icon: Layers, label: 'Pages' },
    { to: '/servers', icon: Server, label: 'Servers' },
    { to: '/notifications', icon: Bell, label: 'Notifications', badge: unreadCount, locked: !canAccess('notifications'), lockedPlan: requiredPlan('notifications') },
    { to: '/domains', icon: Globe, label: 'Domains' },
    { to: '/settings', icon: Settings, label: 'Settings' },
    { to: '/profile', icon: User, label: 'Profile' },
  ];

  return (
    <div className="flex h-screen bg-content-bg text-text-primary">
      {/* Global Snowfall Effect */}
      {snowEnabled && (
        <Snowfall
          style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          snowflakeCount={50}
          speed={[0.5, 1.5]}
          wind={[-0.5, 1]}
          radius={[0.5, 2]}
        />
      )}
      {/* Sidebar */}
      <aside className="w-64 flex flex-col h-full px-4 border-r border-border-default bg-sidebar-bg">
        {/* Logo / Cube - больше места */}
        <div className="py-10 px-4 flex items-center justify-center border-b border-border-default mb-6">
          <SpinnerCube onClick={toggleTheme} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {navItems.map(({ to, icon: Icon, label, badge, locked, lockedPlan }) => (
            locked ? (
              // Locked tab - show as disabled with lock icon
              <div
                key={to}
                className="nav-link opacity-50 cursor-not-allowed"
                onClick={() => setUpgradeModal({ show: true, feature: label, plan: lockedPlan || 'Pro or Enterprise' })}
              >
                <div className="relative inline-flex">
                  <Icon className="icon" strokeWidth={1.75} />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-white/10 rounded-full flex items-center justify-center">
                    <Lock className="w-2.5 h-2.5 text-white/40" />
                  </div>
                </div>
                <span>{label}</span>
              </div>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
              >
                <div className="relative inline-flex">
                  <Icon className="icon" strokeWidth={1.75} />
                  {badge !== undefined && badge > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-accent-blue rounded-full flex items-center justify-center">
                      <span className="text-[11px] font-bold text-white px-1">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    </div>
                  )}
                </div>
                <span>{label}</span>
              </NavLink>
            )
          ))}

          {/* Admin: Users */}
          {user?.role === 'admin' && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `nav-link ${isActive ? 'active' : ''}`
              }
            >
              <Users className="icon" strokeWidth={1.75} />
              <span>Users</span>
            </NavLink>
          )}

          {/* Admin: Payment Dashboard */}
          {user?.role === 'admin' && (
            <NavLink
              to="/info"
              className={({ isActive }) =>
                `nav-link ${isActive ? 'active' : ''}`
              }
            >
              <Activity className="icon" strokeWidth={1.75} />
              <span>Payment</span>
            </NavLink>
          )}
        </nav>

        {/* Upgrade Card - for non-premium users */}
        {!isPremium && (
          <div className="upgrade-card rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-accent-purple" />
              <span className="font-semibold text-text-primary">Upgrade Plan</span>
            </div>
            <p className="text-sm text-text-muted mb-3">
              Get unlimited servers, domains, and premium features.
            </p>
            <a
              href="https://ikcore.cloud/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2.5 rounded-full font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}
            >
              Upgrade to Pro
            </a>
          </div>
        )}

        {/* User Profile & Logout */}
        <div className="border-t border-border-default py-3">
          <div className="flex items-center justify-between px-3 py-3 rounded-full hover:bg-sidebar-hover-bg transition-colors">
            <div
              className="flex items-center gap-3 cursor-pointer flex-1"
              onClick={() => navigate('/profile')}
              title="Go to Profile"
            >
              <div className="w-10 h-10 rounded-full bg-card-bg border border-border-default flex items-center justify-center">
                <span className="text-lg font-semibold" style={{ color: 'var(--accent-primary)' }}>
                  {user.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-text-primary text-[15px]">{user.username}</span>
                  {['pro', 'enterprise', 'admin'].includes(userRole) && (
                    <Crown className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  )}
                </div>
                <span className="text-sm text-text-muted capitalize">{userRole}</span>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-full hover:bg-accent-red/10 text-text-muted hover:text-accent-red transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-6">
        <Outlet />
      </main>

      {/* Upgrade Required Modal */}
      {upgradeModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setUpgradeModal({ show: false, feature: '', plan: '' })}>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/5 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white/40" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {upgradeModal.feature} Locked
            </h3>
            <p className="text-white/50 text-sm mb-6">
              This feature requires {upgradeModal.plan} plan to access.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setUpgradeModal({ show: false, feature: '', plan: '' })}
                className="flex-1 py-2.5 text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors text-sm"
              >
                Cancel
              </button>
              <a
                href="https://ikcore.cloud/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                Upgrade
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="h-screen flex items-center justify-center bg-content-bg">
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
  </div>
);

function App() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route element={<AuthenticatedLayout />}>
        <Route path="/" element={<DashboardPanel />} />
        <Route path="/generator" element={<GeneratorPanel />} />
        <Route path="/pages" element={<PagesPanel />} />
        <Route path="/servers" element={<ServersPanel />} />
        <Route path="/notifications" element={<NotificationsPanel />} />
        <Route path="/domains" element={<DomainsPanel />} />
        <Route path="/settings" element={<SettingsPanel />} />
        <Route path="/profile" element={<ProfilePanel />} />
        <Route path="/users" element={<UsersPanel />} />
        <Route path="/info" element={<InfoPanel />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;